import type { TDirectionInstruction } from "@mappedin/mappedin-js";
import { t } from "./i18n";

const BEARING_KEY: Record<string, "dirContinueStraight" | "dirTurnRight" | "dirBearRight" | "dirTurnLeft" | "dirBearLeft" | "dirTurnAround"> = {
  Straight: "dirContinueStraight",
  Right: "dirTurnRight",
  SlightRight: "dirBearRight",
  Left: "dirTurnLeft",
  SlightLeft: "dirBearLeft",
  Back: "dirTurnAround",
};

export type DirectionIconType =
  | "straight"
  | "turn-right"
  | "slight-right"
  | "turn-left"
  | "slight-left"
  | "u-turn"
  | "elevator"
  | "escalator"
  | "stairs"
  | "connection"
  | "arrival"
  | "depart";

export interface FormattedInstruction {
  text: string;
  icon: DirectionIconType;
  distanceMeters: number;
}

export function instructionText(instruction: TDirectionInstruction, index: number): string {
  const { action, distance } = instruction;
  const meters = Math.round(distance);
  const walk = meters > 0 ? ` (${meters} m)` : "";

  switch (action.type) {
    case "Departure":
      return t("dirHeadOut", { walk });
    case "Turn":
      return t(BEARING_KEY[action.bearing ?? "Straight"] ?? "dirContinueGeneric", { walk });
    case "TakeConnection": {
      const kind = action.connection?.type ?? "connection";
      const dir = action.direction && action.direction !== "none" ? ` ${action.direction}` : "";
      const toFloor = action.toFloor?.name ? ` to ${action.toFloor.name}` : "";
      return t("dirTakeThe", { kind, dir, toFloor });
    }
    case "ExitConnection": {
      const kind = action.connection?.type ?? "connection";
      return t("dirExitThe", { kind, walk });
    }
    case "Arrival":
      return t("dirArrived");
    default:
      return t("dirStep", { n: index + 1, walk });
  }
}

export function instructionIcon(instruction: TDirectionInstruction): DirectionIconType {
  const { action } = instruction;
  switch (action.type) {
    case "Departure":
      return "depart";
    case "Arrival":
      return "arrival";
    case "Turn": {
      switch (action.bearing) {
        case "Right":
          return "turn-right";
        case "SlightRight":
          return "slight-right";
        case "Left":
          return "turn-left";
        case "SlightLeft":
          return "slight-left";
        case "Back":
          return "u-turn";
        default:
          return "straight";
      }
    }
    case "TakeConnection":
    case "ExitConnection": {
      const type = (action.connection?.type ?? "").toLowerCase();
      if (type.includes("elevator")) return "elevator";
      if (type.includes("escalator")) return "escalator";
      if (type.includes("stair")) return "stairs";
      return "connection";
    }
    default:
      return "straight";
  }
}

export function buildDetailedInstructions(instructions: TDirectionInstruction[]): FormattedInstruction[] {
  return instructions.map((step, i) => ({
    text: instructionText(step, i),
    icon: instructionIcon(step),
    distanceMeters: Math.round(step.distance),
  }));
}

export function buildInstructionList(instructions: TDirectionInstruction[]): string[] {
  return instructions.map((step, i) => instructionText(step, i));
}
